import React, {useEffect} from 'react';
import Head from '../../Components/Profile/Head';
import {useTranslation} from 'react-i18next';
import './UserDictionaries.scss';
import {ButtonMUI, IconButtonMUI, InputMUI, SelectComponent} from '../../../shared';
import {Controller, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {addProjectType, getProjectTypes} from '../../../redux/actions/projects/projectsActions';
import {addCity, addCountry, getCities, getCountries} from '../../../redux/actions/countries/countriesActions';
import {notifyError, notifySuccess} from '../../../helpers/notifySnack';
import {useDispatch, useSelector} from 'react-redux';
import LoadPanel from '../../Components/LoadPanel';
import {capitalizeFirstLetter} from '../../../helpers/constants';
import {ReactComponent as EditSvg} from '../../../assets/icons/edit.svg';
import {setDictionariesEdit} from '../../../redux/actions/dictionaries/dictionariesActions';
import UserDictionariesPopup from './UserDictionariesPopup';
const UserDictionaries = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {countriesLoading, countries, cities, citiesLoading} = useSelector(({countries}) => countries);
  const {projectTypes, projectTypesLoading} = useSelector(({projects}) => projects);

  const options = [
    {id: 1, name: t('dictionaries.country')},
    {id: 2, name: t('dictionaries.city')},
    {id: 3, name: t('dictionaries.type')}
  ];

  const schema = yup.object().shape({
    type: yup.number().required(t('validations.required')),
    country: yup.string().when('type', {
      is: (val) => val === 1, // Якщо обрано тип "Країна" або "Місто"
      then: yup.string().required(t('dictionaries.enterCountry'))
    }),
    city: yup.string().when('type', {
      is: (val) => val === 2, // Якщо обрано тип "Місто"
      then: yup.string().required(t('dictionaries.enterNameCity'))
    }),
    countryId: yup.string().when('type', {
      is: (val) => val === 2, // Якщо обрано тип "Місто"
      then: yup.string().required(t('dictionaries.countryChoose'))
    }),
    projectType: yup.string().when('type', {
      is: (val) => val === 3, // Якщо обрано тип "Тип проекту"
      then: yup.string().required(t('dictionaries.enterType'))
    })
  });

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: {errors, isValid, isSubmitting}
  } = useForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      country: '',
      countryId: '',
      city: '',
      projectType: ''
    }
  });

  const selectedType = watch('type'); // Використовуємо watch для відстеження вибраного типу

  const onSubmit = async (data) => {
    const formData = new FormData();

    if (selectedType === 1) {
      formData.append('name', capitalizeFirstLetter(data.country));

      const res = await dispatch(addCountry(formData));

      if (res.payload) {
        dispatch(getCountries());
        notifySuccess(t('all.successfully'));
        reset({
          type: 1,
          country: '',
          countryId: '',
          city: '',
          projectType: ''
        });
      }
    }
    if (selectedType === 2) {
      formData.append('countryId', data.countryId);
      formData.append('name', capitalizeFirstLetter(data.city));

      const res = await dispatch(addCity(formData));

      if (res.payload) {
        dispatch(getCities());
        reset({
          type: 2,
          country: '',
          countryId: '',
          city: '',
          projectType: ''
        });
        notifySuccess(t('all.successfully'));
      }
    }
    if (selectedType === 3) {
      formData.append('name', capitalizeFirstLetter(data.projectType));

      const res = await dispatch(addProjectType(formData));

      if (res.payload) {
        dispatch(getProjectTypes());
        reset({
          type: 3,
          country: '',
          countryId: '',
          city: '',
          projectType: ''
        });
        notifySuccess(t('all.successfully'));
      }
    }
  };

  useEffect(() => {
    const fetchFunc = async () => {
      try {
        await dispatch(getProjectTypes());

        await dispatch(getCountries());

        await dispatch(getCities());
      } catch (error) {
        notifyError(error.message || 'error');
      }
    };

    fetchFunc();
  }, []);

  useEffect(() => {
    setValue('country', '');
    setValue('countryId', '');
    setValue('city', '');
    setValue('projectType', '');
  }, [selectedType]);

  const showEditPopup = (data) => {
    dispatch(
      setDictionariesEdit({
        visible: true,
        type: selectedType,
        data
      })
    );
  };
  return (
    <div className='user-dictionaries'>
      <Head title={t('profile.catalog')} />
      {countriesLoading || citiesLoading || projectTypesLoading ? (
        <LoadPanel noBg={true} staticPos={true} circleSize={40} />
      ) : (
        <>
          <div className='user-dictionaries__content'>
            <div className='user-dictionaries__input'>
              <Controller
                name='type'
                control={control}
                render={({field}) => (
                  <SelectComponent
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    value={options.find((c) => c.name === field.value)}
                    options={options}
                    allowClear={false}
                    label={t('dictionaries.choose')}
                    inputProps={field}
                    error={errors.type?.message}
                    fullWidth={true}
                  />
                )}
              />
            </div>
            {selectedType && (
              <>
                <div className='user-dictionaries__tables'>
                  {selectedType === 1 && (
                    <>
                      <h4>{t('dictionaries.countryList')}</h4>
                      <table className={'user-dictionaries__table'}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('dictionaries.country')}</th>
                            <th className={'user-dictionaries__actions'}>{t('dictionaries.action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {countries?.map((el, index) => {
                            return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{el.name}</td>
                                <td
                                  className={'td-edit'}
                                  onClick={() => {
                                    showEditPopup(el);
                                  }}
                                >
                                  <IconButtonMUI title={t('dictionaries.edit')}>
                                    <EditSvg />
                                  </IconButtonMUI>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                  {selectedType === 2 && (
                    <>
                      <h4>{t('dictionaries.cityList')}</h4>
                      <table className={'user-dictionaries__table'}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('dictionaries.country')}</th>
                            <th>{t('dictionaries.city')}</th>
                            <th className={'user-dictionaries__actions'}>{t('dictionaries.action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cities?.map((el, index) => {
                            const foundCountry = countries.find((country) => country.id === el.countryId);
                            return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{foundCountry?.name ?? ''}</td>
                                <td>{el.name}</td>
                                <td
                                  className={'td-edit'}
                                  onClick={() => {
                                    showEditPopup(el);
                                  }}
                                >
                                  <IconButtonMUI title={t('dictionaries.edit')}>
                                    <EditSvg />
                                  </IconButtonMUI>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                  {selectedType === 3 && (
                    <>
                      <h4>{t('dictionaries.typesList')}</h4>
                      <table className={'user-dictionaries__table'}>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>{t('dictionaries.type')}</th>
                            <th className={'user-dictionaries__actions'}>{t('dictionaries.action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectTypes?.map((el, index) => {
                            return (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{el.name}</td>
                                <td
                                  className={'td-edit'}
                                  onClick={() => {
                                    showEditPopup(el);
                                  }}
                                >
                                  <IconButtonMUI title={t('dictionaries.edit')}>
                                    <EditSvg />
                                  </IconButtonMUI>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className='user-dictionaries__choices'>
                  {selectedType === 1 && (
                    <>
                      <h3>{t('dictionaries.addCountry')}</h3>
                      <div className='user-dictionaries__input'>
                        <Controller
                          name='country'
                          control={control}
                          render={({field}) => (
                            <InputMUI
                              label={t('dictionaries.countryName')}
                              value={field.value}
                              fullWidth={true}
                              error={errors.country?.message}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[0-9]/g, '');
                                field.onChange(value);
                              }}
                              inputProps={{
                                ...field,
                                pattern: '[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\\s]*'
                              }}
                              className={'no-icons'}
                            />
                          )}
                        />
                      </div>
                    </>
                  )}
                  {selectedType === 2 && (
                    <>
                      <h3>{t('dictionaries.addCity')}</h3>
                      <div className='user-dictionaries__input'>
                        <Controller
                          name='countryId'
                          control={control}
                          render={({field}) => (
                            <SelectComponent
                              label={t('dictionaries.countryChoose')}
                              onChange={field.onChange}
                              options={countries}
                              value={countries.find((c) => c.id === field.value)}
                              error={errors.countryId?.message}
                              fullWidth={true}
                              className={'no-icons'}
                              inputProps={field}
                            />
                          )}
                        />
                      </div>
                      <div className='user-dictionaries__input'>
                        <Controller
                          name='city'
                          control={control}
                          render={({field}) => (
                            <InputMUI
                              label={t('dictionaries.enterNameCity')}
                              value={field.value}
                              error={errors.city?.message}
                              fullWidth={true}
                              className={'no-icons'}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[0-9]/g, '');
                                field.onChange(value);
                              }}
                              inputProps={{
                                ...field,
                                pattern: '[A-Za-zА-Яа-яЁёЇїІіЄєҐґ\\s]*'
                              }}
                            />
                          )}
                        />
                      </div>
                    </>
                  )}
                  {selectedType === 3 && (
                    <>
                      <h3>{t('dictionaries.addType')}</h3>
                      <div className='user-dictionaries__input'>
                        <Controller
                          name='projectType'
                          control={control}
                          render={({field}) => (
                            <InputMUI
                              label={t('dictionaries.enterType')}
                              onChange={field.onChange}
                              value={field.value}
                              error={errors.projectType?.message}
                              fullWidth={true}
                              className={'no-icons'}
                            />
                          )}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className={'user-dictionaries__submit'}>
                  <ButtonMUI disabled={!isValid} loading={isSubmitting} type='submit' onClick={handleSubmit(onSubmit)}>
                    {t('dictionaries.send')}
                  </ButtonMUI>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <UserDictionariesPopup />
    </div>
  );
};

export default UserDictionaries;
